import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';

import server from '../../../api/server';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import SearchInput from '../ui/SearchInput';
import FileThumb from './FileThumb';
import FilePreviewModal from './FilePreviewModal';
import { getMimeType, getUploadStamp, getUploadMillis, formatBytes, isPreviewable } from './fileHelpers';
import {
    DownloadIcon,
    ChevronDownIcon,
    GridIcon,
    ListIcon,
    EyeIcon,
} from '../ui/icons';

// Redesign of newadmin/elements/Documents.js — same /docs/download,
// /docs/all and /bankstatements/data requests. Adds real image/PDF previews
// (lazily fetched), grid/list views, search and a lightbox so admins can scan
// large uploads (60+ files) without expanding every category.
const DocumentsApplicant = ({ applicantId }) => {
    const documents = useSelector((s) => s.admin.active_application.documents);
    const bankstatements = useSelector((s) => s.admin.active_application.bankstatements);
    const activeApplication = useSelector((s) => s.admin.active_application);
    const user = useSelector((s) => s.user);

    // Kept for parity with the original (fetched on mount, not rendered).
    const [, setData] = useState(null);

    // Remember the grid/list choice and sort order per logged-in admin so they
    // stick between applicants and sessions.
    const viewPrefKey = `admin2:docsView:${user.email || 'default'}`;
    const sortPrefKey = `admin2:docsSort:${user.email || 'default'}`;
    const readPref = (key, allowed, fallback) => {
        try {
            const saved = window.localStorage.getItem(key);
            return allowed.includes(saved) ? saved : fallback;
        } catch (e) {
            return fallback;
        }
    };
    const [query, setQuery] = useState('');
    const [viewMode, setViewMode] = useState(() =>
        readPref(viewPrefKey, ['grid', 'list'], 'grid')
    );
    const [sort, setSort] = useState(() =>
        readPref(sortPrefKey, ['recent', 'oldest', 'name'], 'recent')
    );
    const [showMissing, setShowMissing] = useState(false);

    useEffect(() => {
        try {
            window.localStorage.setItem(viewPrefKey, viewMode);
        } catch (e) {
            /* localStorage unavailable — fall back to in-memory only */
        }
    }, [viewMode, viewPrefKey]);

    useEffect(() => {
        try {
            window.localStorage.setItem(sortPrefKey, sort);
        } catch (e) {
            /* localStorage unavailable — fall back to in-memory only */
        }
    }, [sort, sortPrefKey]);
    const [collapsed, setCollapsed] = useState({});
    const [previewIndex, setPreviewIndex] = useState(null);

    // path -> { blob, url }; shared by thumbnails, the lightbox and downloads
    // so a file is only fetched from storage once.
    const cache = useRef(new Map());

    useEffect(() => {
        const fetchData = async () => {
            if (
                documents.applicants[applicantId].requested_documents == undefined ||
                bankstatements == null
            ) {
                return;
            }
            const response = await server.post('/bankstatements/data', {
                id: user.token,
                application_id: bankstatements.application_id,
                applicant_id: applicantId,
            });
            if (response.data && response.data.success == true) {
                setData(response.data.htmlArray);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(
        () => () => {
            cache.current.forEach((entry) => URL.revokeObjectURL(entry.url));
            cache.current.clear();
        },
        []
    );

    const applicant = activeApplication.application.applicants[applicantId];
    const requestedDocuments = documents.applicants[applicantId]
        ? documents.applicants[applicantId].requested_documents
        : undefined;

    const fetchBlob = async (uploadedFile) => {
        const response = await server.post(
            '/docs/download',
            {
                file_name: uploadedFile.file_name,
                id: user.token,
                file_upload_id: activeApplication.documents.id,
                path: uploadedFile.path,
            },
            { responseType: 'blob' }
        );
        if (!response.data) return null;
        return new Blob([response.data], { type: getMimeType(uploadedFile.file_name) });
    };

    // Returns { url, blob } from cache, fetching + caching on first use.
    const loadPreview = async (uploadedFile) => {
        const key = uploadedFile.path;
        if (cache.current.has(key)) return cache.current.get(key);
        const blob = await fetchBlob(uploadedFile);
        if (!blob) return null;
        const entry = { blob, url: URL.createObjectURL(blob) };
        cache.current.set(key, entry);
        return entry;
    };

    const downloadFile = async (uploadedFile) => {
        const cached = cache.current.get(uploadedFile.path);
        const blob = cached ? cached.blob : await fetchBlob(uploadedFile);
        if (blob) saveAs(blob, uploadedFile.original_name || uploadedFile.file_name);
    };

    const downloadApplicantFiles = async () => {
        const response = await server.post(
            '/docs/all',
            {
                file_upload_id: activeApplication.documents.id,
                id: user.token,
                path: `${documents.id}/${applicantId}`,
            },
            { responseType: 'blob' }
        );
        let blob = new Blob([response.data], { type: 'application/zip' });
        let objectUrl = URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${applicant.name}.zip`;
        link.click();
    };

    // Group requested doc types by category, apply the search filter, and build
    // the flat, in-order list of visible files that the lightbox pages through.
    const { groups, flatFiles, totalFiles, uploadedTypes, totalTypes } = useMemo(() => {
        const result = { groups: [], flatFiles: [], totalFiles: 0, uploadedTypes: 0, totalTypes: 0 };
        if (requestedDocuments == undefined) return result;

        const q = query.trim().toLowerCase();
        const byCategory = {};

        Object.keys(requestedDocuments).forEach((key) => {
            const docType = requestedDocuments[key];
            const category = docType.category != undefined ? docType.category : 'Files';
            const files = docType.files || [];

            result.totalTypes += 1;
            if (files.length > 0) result.uploadedTypes += 1;
            result.totalFiles += files.length;

            const labelMatch =
                !q ||
                (docType.label || '').toLowerCase().includes(q) ||
                category.toLowerCase().includes(q);
            const matched = labelMatch
                ? files
                : files.filter((f) =>
                      (f.original_name || f.file_name || '').toLowerCase().includes(q)
                  );

            // Hide when the search excludes it, or when it's empty and the
            // "show missing" toggle is off.
            if (q && !labelMatch && matched.length === 0) return;
            if (!q && files.length === 0 && !showMissing) return;

            const visibleFiles = [...matched].sort((a, b) => {
                if (sort === 'name') {
                    return (a.original_name || a.file_name || '').localeCompare(
                        b.original_name || b.file_name || ''
                    );
                }
                const diff = getUploadMillis(b) - getUploadMillis(a);
                return sort === 'oldest' ? -diff : diff;
            });

            if (!byCategory[category]) byCategory[category] = [];
            byCategory[category].push({ key, docType, files: visibleFiles });
        });

        result.groups = Object.keys(byCategory).map((category) => ({
            category,
            types: byCategory[category],
        }));

        // Flat order matches render order (category, then doc type, then file).
        result.groups.forEach((group) => {
            group.types.forEach((t) => {
                t.files.forEach((f) => result.flatFiles.push(f));
            });
        });

        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedDocuments, query, showMissing, sort]);

    const flatIndexOf = (file) => flatFiles.findIndex((f) => f.path === file.path);

    const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    if (applicant == undefined) return null;

    const renderFile = (file) => {
        const idx = flatIndexOf(file);
        const stamp = getUploadStamp(file);
        const stampText = [stamp.date, stamp.time].filter(Boolean).join(' · ');
        const previewable = isPreviewable(file.file_name);

        if (viewMode === 'list') {
            return (
                <div
                    key={file.path}
                    className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] px-3 py-2 hover:border-[#7C5CFC] hover:bg-[#FBFAFF] transition-colors"
                >
                    <FileThumb
                        file={file}
                        loadPreview={loadPreview}
                        className="h-10 w-10 flex-shrink-0 rounded-lg"
                        iconClassName="h-5 w-5"
                    />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#1F2937]">
                            {file.original_name || file.file_name}
                        </p>
                        <p className="truncate text-xs text-[#6B7280]">
                            {[stampText, file.size ? formatBytes(file.size) : null]
                                .filter(Boolean)
                                .join(' · ')}
                        </p>
                    </div>
                    {previewable ? (
                        <button
                            type="button"
                            onClick={() => setPreviewIndex(idx)}
                            title="Preview"
                            className="rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors"
                        >
                            <EyeIcon className="h-4 w-4" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => downloadFile(file)}
                        title="Download"
                        className="rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors"
                    >
                        <DownloadIcon className="h-4 w-4" />
                    </button>
                </div>
            );
        }

        return (
            <div
                key={file.path}
                role="button"
                tabIndex={0}
                onClick={() => (previewable ? setPreviewIndex(idx) : downloadFile(file))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        previewable ? setPreviewIndex(idx) : downloadFile(file);
                    }
                }}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-[#E5E7EB] text-left hover:border-[#7C5CFC] hover:shadow-sm transition-all"
                title={previewable ? `Preview ${file.file_name}` : `Download ${file.file_name}`}
            >
                <div className="relative h-24 w-full">
                    <FileThumb file={file} loadPreview={loadPreview} className="h-full w-full" iconClassName="h-7 w-7" />
                    <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {previewable ? (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewIndex(idx);
                                }}
                                title="Preview"
                                className="rounded-md bg-white/90 p-1 text-[#5B3FE0] shadow-sm hover:bg-white"
                            >
                                <EyeIcon className="h-3.5 w-3.5" />
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                downloadFile(file);
                            }}
                            title="Download"
                            className="rounded-md bg-white/90 p-1 text-[#5B3FE0] shadow-sm hover:bg-white"
                        >
                            <DownloadIcon className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
                <div className="px-2 py-1.5">
                    <p className="truncate text-[11px] font-semibold text-[#1F2937]">
                        {file.original_name || file.file_name}
                    </p>
                    {stampText ? (
                        <p className="truncate text-[10px] text-[#9CA3AF]">{stampText}</p>
                    ) : null}
                </div>
            </div>
        );
    };

    const renderDocType = ({ key, docType, files }) => {
        const isOpen = !collapsed[key];
        const empty = files.length === 0;
        return (
            <div key={key} className="rounded-xl border border-[#E5E7EB] overflow-hidden">
                <button
                    type="button"
                    onClick={() => toggle(key)}
                    className="flex w-full items-center justify-between gap-3 bg-[#F5F6FA] px-4 py-3 text-left text-sm font-semibold text-[#1F2937] hover:bg-[#EFEAFE] transition-colors"
                >
                    <span className="flex items-center gap-2">
                        {docType.label}
                        <span
                            className="font-bold"
                            style={{ color: empty ? '#DC2626' : '#16A34A' }}
                        >
                            ({files.length})
                        </span>
                    </span>
                    <ChevronDownIcon
                        className={`h-4 w-4 text-[#6B7280] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                </button>
                {isOpen ? (
                    <div className="px-4 py-4">
                        {empty ? (
                            <p className="text-sm text-[#9CA3AF]">Awaiting upload from your client.</p>
                        ) : viewMode === 'grid' ? (
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                    gap: '12px',
                                }}
                            >
                                {files.map(renderFile)}
                            </div>
                        ) : (
                            <div className="space-y-2">{files.map(renderFile)}</div>
                        )}
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <div className="py-4 border-b border-[#F0F1F5] last:border-b-0">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <h4 className="text-base font-bold text-[#1F2937]">
                        {applicant.name}{' '}
                        <span className="text-sm font-normal text-[#6B7280]">Files</span>
                    </h4>
                    {requestedDocuments != undefined ? (
                        <Badge tone={totalFiles > 0 ? 'accent' : 'neutral'}>
                            {totalFiles} file{totalFiles === 1 ? '' : 's'} · {uploadedTypes}/{totalTypes} types
                        </Badge>
                    ) : null}
                </div>
                {requestedDocuments != undefined ? (
                    <Button variant="secondary" onClick={downloadApplicantFiles}>
                        <DownloadIcon className="h-4 w-4" />
                        Download Applicant Files
                    </Button>
                ) : null}
            </div>

            {requestedDocuments == undefined ? (
                <p className="text-sm text-[#6B7280]">
                    You have not requested any documents for this applicant.
                </p>
            ) : (
                <>
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <SearchInput
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search files..."
                            className="min-w-[180px] flex-1"
                        />
                        <div className="inline-flex overflow-hidden rounded-xl border border-[#E5E7EB]">
                            <button
                                type="button"
                                onClick={() => setViewMode('grid')}
                                aria-label="Grid view"
                                className={`p-2.5 transition-colors ${
                                    viewMode === 'grid'
                                        ? 'bg-[#EFEAFE] text-[#5B3FE0]'
                                        : 'bg-white text-[#6B7280] hover:bg-[#F5F6FA]'
                                }`}
                            >
                                <GridIcon className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                aria-label="List view"
                                className={`border-l border-[#E5E7EB] p-2.5 transition-colors ${
                                    viewMode === 'list'
                                        ? 'bg-[#EFEAFE] text-[#5B3FE0]'
                                        : 'bg-white text-[#6B7280] hover:bg-[#F5F6FA]'
                                }`}
                            >
                                <ListIcon className="h-4 w-4" />
                            </button>
                        </div>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            aria-label="Sort files"
                            className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm font-semibold text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
                        >
                            <option value="recent">Most recent</option>
                            <option value="oldest">Oldest first</option>
                            <option value="name">Name (A-Z)</option>
                        </select>
                        <button
                            type="button"
                            onClick={() => setShowMissing((v) => !v)}
                            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                                showMissing
                                    ? 'border-[#7C5CFC] bg-[#EFEAFE] text-[#5B3FE0]'
                                    : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F5F6FA]'
                            }`}
                        >
                            {showMissing ? 'Hide missing' : 'Show missing'}
                        </button>
                    </div>

                    {groups.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF]">
                            {query
                                ? 'No files match your search.'
                                : 'No documents uploaded yet.'}
                        </p>
                    ) : (
                        <div className="space-y-5">
                            {groups.map((group) => (
                                <div key={group.category}>
                                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#6B7280]">
                                        {group.category}
                                    </p>
                                    <div className="space-y-2">
                                        {group.types.map(renderDocType)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            <FilePreviewModal
                files={flatFiles}
                index={previewIndex}
                onIndex={setPreviewIndex}
                onClose={() => setPreviewIndex(null)}
                loadPreview={loadPreview}
                downloadFile={downloadFile}
            />
        </div>
    );
};

export default DocumentsApplicant;
